import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
    Box,
    Button,
    Chip,
    Grid,
    Hidden,
    LinearProgress,
    Stack,
    styled,
    Typography,
} from '@mui/material';
import { compareAsc, compareDesc, format, parseISO } from 'date-fns';
import React, { useState } from 'react';
import Magnify from '../assets/magnify.png';
import quickBookImage from '../assets/quickBook.png';
import RightGrowNav from './BasicComponents/RightGrowNav';
import StatusIndicator from './BasicComponents/StatusIndicator';
import TitleCase from './BasicComponents/TitleCase';
import SimpleSnackbar from './SnackBar/SimpleSnackbar';

const Image = styled('img')(({ theme }) => ({
    height: '40px',
    borderRadius: '5%',
    [theme.breakpoints.down('md')]: {
        height: '90px',
    },
    [theme.breakpoints.down('sm')]: {
        height: 'auto',
        width: '100%',
        marginTop: '0px',
        marginRight: '0px',
    },
}));

const QBImage = styled('img')(({ theme }) => ({
    height: '40px',
    borderRadius: '5%',
    [theme.breakpoints.down('md')]: {
        height: '70px',
    },
    [theme.breakpoints.down('sm')]: {
        height: 'auto',
        width: '100%',
        marginTop: '0px',
        marginRight: '0px',
    },
}));

const IconImage = styled('img')(({ theme }) => ({
    height: '40px',
    borderRadius: '5%',
}));

const AdjustStack = styled(Stack)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        marginTop: '6px',
        'background-image': 'linear-gradient(to right, #ffffff, #3d7aa4)',
        width: '100%',
    },
}));

const GridRow = styled(Grid)(({ theme }) => ({
    minHeight: '65px',
    [theme.breakpoints.down('sm')]: {
        minHeight: '105px',
    },
}));

const ChipSpaced = styled(Chip)(({ theme }) => ({
    margin: '2px',
}));

const GridItem = styled(Grid)(({ theme }) => ({
    margin: 'auto',
}));

const ImageGrid = styled(Grid)(({ theme }) => ({
    [theme.breakpoints.down('md')]: {
        margin: '10px',
    },
    margin: 'auto',
    padding: '0px',
    [theme.breakpoints.down('sm')]: {
        margin: 'auto',
    },
}));

const StyledBox = styled(Box)(({ theme }) => ({
    color: theme.palette.secondary.main,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    margin: 0,
    borderRadius: '16px',
}));

const HeaderGridItem = styled(Grid)(({ theme }) => ({
    margin: '10px 0px',
}));

const HeaderTypo = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    textAlign: 'center',
}));

const RootBox = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    backgroundColor: '#FFF',
    cursor: 'pointer',

    [theme.breakpoints.up('sm')]: {
        borderTop: 'solid #e7e7e7 1px',
    },
    [theme.breakpoints.down('md')]: {
        marginTop: '12px',
        marginBottom: '12px',
        paddingTop: '8px',
        boxShadow: '1px 1px 9px -2px rgb(0 0 0 / 20%)',
    },
}));

const BoxShadow = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        boxShadow: '1px 1px 9px -2px rgb(0 0 0 / 20%)',
    },
}));

const QuickBooking = props => {
    return (
        <div>
            <Button color="primary" variant="text">
                <BoltIcon></BoltIcon>
                Quick Booking
            </Button>
            {props.extraText === true && (
                <Typography variant="body2">
                    Awaiting Teachers Response
                </Typography>
            )}
        </div>
    );
};

export const HistoryTable = props => {
    //props
    //title - Name of the table
    //rows - data to be used in table
    //confirmBooking - Submit a booking approval or decline
    //navigateToDetails - Navigate to the details page
    //type - if your a school or a teacher

    let sortedRows = props.rows;

    const [dateSort, setDateSort] = useState(1);
    const [statusSort, setStatusSort] = useState(0);
    const [schoolSort, setSchoolSort] = useState(0);

    function sortByDate() {
        if (dateSort === 1) {
            setDateSort(2);
        } else {
            setDateSort(1);
        }
        setStatusSort(0);
        setSchoolSort(0);
    }

    function sortByStatus() {
        if (statusSort === 1) {
            setStatusSort(2);
        } else {
            setStatusSort(1);
        }
        setDateSort(0);
        setSchoolSort(0);
    }

    function sortBySchool() {
        if (statusSort === 1) {
            setSchoolSort(2);
        } else {
            setSchoolSort(1);
        }
        setDateSort(0);
        setStatusSort(0);
    }

    if (dateSort === 1) {
        sortedRows = props.rows.sort((a, b) =>
            a.startDate < b.startDate ? 1 : -1
        );
    } else if (dateSort === 2) {
        sortedRows = props.rows.sort((a, b) =>
            a.startDate > b.startDate ? 1 : -1
        );
    } else if (statusSort === 1) {
        sortedRows = props.rows.sort((a, b) => (a.status < b.status ? 1 : -1));
    } else if (statusSort === 2) {
        sortedRows = props.rows.sort((a, b) => (a.status > b.status ? 1 : -1));
    } else if (schoolSort === 1) {
        sortedRows = props.rows.sort((a, b) =>
            a.schoolName < b.schoolName ? 1 : -1
        );
    } else if (schoolSort === 2) {
        sortedRows = props.rows.sort((a, b) =>
            a.schoolName > b.schoolName ? 1 : -1
        );
    }

    return (
        <Grid className="full">
            <Grid
                container
                directon="row"
                justifyContent="space-around"
                alignItems="center"
            >
                <Grid
                    item
                    lg={10}
                    md={12}
                    sm={12}
                    xs={12}
                    sx={{ paddingBottom: 4 }}
                >
                    <StyledBox sx={{ textAlign: 'right' }}>
                        <Typography variant="body1" sx={{ margin: '10px 0px' }}>
                            {sortedRows.length !== 1
                                ? `${sortedRows.length} items found`
                                : `${sortedRows.length} item found`}
                        </Typography>
                    </StyledBox>
                    <BoxShadow
                        sx={{
                            borderRadius: '5px',
                        }}
                    >
                        {/* Show header on Med - XL screens */}
                        <Hidden only={['xs', 'sm']}>
                            <StyledBox>
                                <BoxShadow>
                                    <Grid container>
                                        <Grid
                                            item
                                            xs={6}
                                            sm={2}
                                            md={1}
                                            lg={1}
                                        ></Grid>
                                        <HeaderGridItem
                                            item
                                            xs={6}
                                            sm={2}
                                            md={1}
                                            lg={1}
                                        >
                                            <HeaderTypo
                                                variant="body1"
                                                onClick={sortByDate}
                                            >
                                                Date
                                                {dateSort === 1 && (
                                                    <ArrowDropDownIcon />
                                                )}
                                                {dateSort === 2 && (
                                                    <ArrowDropUpIcon />
                                                )}
                                            </HeaderTypo>
                                        </HeaderGridItem>
                                        <HeaderGridItem
                                            item
                                            xs={6}
                                            sm={3}
                                            md={1}
                                            lg={1}
                                        >
                                            <HeaderTypo variant="body1">
                                                Start Time
                                            </HeaderTypo>
                                        </HeaderGridItem>
                                        <HeaderGridItem
                                            item
                                            xs={6}
                                            sm={3}
                                            md={1}
                                            lg={1}
                                        >
                                            <HeaderTypo variant="body1">
                                                End Time
                                            </HeaderTypo>
                                        </HeaderGridItem>
                                        <HeaderGridItem
                                            item
                                            xs={6}
                                            sm={3}
                                            md={3}
                                            lg={3}
                                        >
                                            {props.type === 'School' ? (
                                                <HeaderTypo
                                                    variant="body1"
                                                    onClick={sortBySchool}
                                                >
                                                    Teacher Name
                                                    {schoolSort === 1 && (
                                                        <ArrowDropUpIcon />
                                                    )}
                                                    {schoolSort === 2 && (
                                                        <ArrowDropDownIcon />
                                                    )}
                                                </HeaderTypo>
                                            ) : (
                                                <HeaderTypo
                                                    variant="body1"
                                                    onClick={sortBySchool}
                                                >
                                                    School Name
                                                    {schoolSort === 1 && (
                                                        <ArrowDropUpIcon />
                                                    )}
                                                    {schoolSort === 2 && (
                                                        <ArrowDropDownIcon />
                                                    )}
                                                </HeaderTypo>
                                            )}
                                        </HeaderGridItem>
                                        <HeaderGridItem
                                            item
                                            xs={6}
                                            sm={3}
                                            md={2}
                                            lg={2}
                                        >
                                            <HeaderTypo
                                                variant="body1"
                                                onClick={sortByStatus}
                                            >
                                                Status
                                                {statusSort === 1 && (
                                                    <ArrowDropUpIcon />
                                                )}
                                                {statusSort === 2 && (
                                                    <ArrowDropDownIcon />
                                                )}
                                            </HeaderTypo>
                                        </HeaderGridItem>
                                    </Grid>
                                </BoxShadow>
                            </StyledBox>
                        </Hidden>
                        {sortedRows.length < 1 && (
                            <Stack
                                direction="column"
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ padding: '20px' }}
                                >{`No items found`}</Typography>
                                <IconImage
                                    src={Magnify}
                                    sx={{ marginBottom: '20px' }}
                                />
                            </Stack>
                        )}
                        {sortedRows.map((row, index) => (
                            <StyledBox key={row.uniqueId}>
                                <RootBox
                                    onClick={event => {
                                        event.preventDefault();
                                        props.navigateToDetails(row.uniqueId);
                                    }}
                                >
                                    <GridRow container data-cy="history-row">
                                        {/* Show the calendar icon on xs and small screens */}
                                        <Hidden only={['md', 'lg', 'xl']}>
                                            <GridItem
                                                item
                                                xs={2}
                                                sm={1}
                                                md={1}
                                                lg={1}
                                                data-cy="date"
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="h3"
                                                        sx={{
                                                            margin: '0px',
                                                        }}
                                                    >
                                                        {format(
                                                            parseISO(
                                                                row.startDate
                                                            ),
                                                            'dd'
                                                        )}
                                                    </Typography>
                                                    <Typography variant="body1">
                                                        {format(
                                                            parseISO(
                                                                row.startDate
                                                            ),
                                                            'MMM'
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </GridItem>
                                        </Hidden>
                                        <Hidden only={['xs']}>
                                            <ImageGrid
                                                item
                                                xs={3}
                                                sm={2}
                                                md={1}
                                                lg={1}
                                            >
                                                {props.type === 'School' ? (
                                                    <div>
                                                        {typeof row.type !==
                                                            'undefined' &&
                                                        row.type ===
                                                            'quickBook' ? (
                                                            <Box padding={1}>
                                                                <QBImage
                                                                    src={
                                                                        quickBookImage
                                                                    }
                                                                ></QBImage>
                                                            </Box>
                                                        ) : (
                                                            <Image
                                                                src={
                                                                    row.teacherProfile
                                                                }
                                                                sx={{
                                                                    borderRadius:
                                                                        '5%',
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                ) : (
                                                    <Image
                                                        src={row.schoolLogo}
                                                        sx={{
                                                            borderRadius: '5%',
                                                        }}
                                                    />
                                                )}
                                            </ImageGrid>
                                        </Hidden>
                                        {/* Show the date ddMMyyyy on med - xl screens */}
                                        <Hidden only={['sm', 'xs']}>
                                            <GridItem
                                                item
                                                xs={6}
                                                sm={2}
                                                md={1}
                                                lg={1}
                                                data-cy="date"
                                            >
                                                <Typography variant="body1">
                                                    {format(
                                                        parseISO(row.startDate),
                                                        'dd MMM yyyy'
                                                    )}
                                                </Typography>
                                            </GridItem>
                                        </Hidden>
                                        {/* Show on small screens */}

                                        {/* Show on start and end time individually on med - xl screens */}
                                        <Hidden only={['xs', 'sm']}>
                                            <GridItem item md={1} lg={1}>
                                                <Typography
                                                    variant="body1"
                                                    data-cy="start-time"
                                                >
                                                    {format(
                                                        parseISO(row.startDate),
                                                        'h:mma'
                                                    )}
                                                </Typography>
                                            </GridItem>
                                            <GridItem item md={1} lg={1}>
                                                <Typography
                                                    variant="body1"
                                                    data-cy="end-time"
                                                >
                                                    {format(
                                                        parseISO(row.endDate),
                                                        'h:mma'
                                                    )}
                                                </Typography>
                                            </GridItem>
                                        </Hidden>
                                        {/* Show the teacher name or school name on med - xl screens */}
                                        <Hidden only={['xs', 'sm']}>
                                            {typeof row.type !== 'undefined' &&
                                            row.type === 'quickBook' ? (
                                                <GridItem
                                                    item
                                                    xs={6}
                                                    sm={3}
                                                    md={3}
                                                    lg={3}
                                                >
                                                    <QuickBooking />
                                                </GridItem>
                                            ) : (
                                                <GridItem
                                                    item
                                                    xs={6}
                                                    sm={3}
                                                    md={3}
                                                    lg={3}
                                                >
                                                    {props.type === 'School' ? (
                                                        <TitleCase
                                                            data-cy="teacher-name"
                                                            string={
                                                                row.teacherFirstName +
                                                                ' ' +
                                                                row.teacherLastName
                                                            }
                                                            variant="body1"
                                                        />
                                                    ) : (
                                                        <TitleCase
                                                            string={
                                                                row.schoolName
                                                            }
                                                            variant="body1"
                                                        />
                                                    )}
                                                </GridItem>
                                            )}
                                        </Hidden>
                                        {/* Show teacher/school name, start time and finish time and status and navigation button on small screen */}
                                        <Hidden only={['xs', 'md', 'lg', 'xl']}>
                                            {typeof row.type !== 'undefined' &&
                                            row.type === 'quickBook' ? (
                                                <GridItem
                                                    item
                                                    xs={6}
                                                    sm={6}
                                                    md={2}
                                                    lg={2}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    <QuickBooking
                                                        extraText={true}
                                                    />
                                                </GridItem>
                                            ) : (
                                                <GridItem
                                                    item
                                                    xs={6}
                                                    sm={6}
                                                    md={2}
                                                    lg={2}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    {props.type === 'School' ? (
                                                        <TitleCase
                                                            variant="h6"
                                                            data-cy="teacher-name"
                                                            sx={{
                                                                marginBottom:
                                                                    '4px',
                                                            }}
                                                            string={
                                                                row.teacherFirstName +
                                                                ' ' +
                                                                row.teacherLastName
                                                            }
                                                        />
                                                    ) : (
                                                        <TitleCase
                                                            variant="h6"
                                                            sx={{
                                                                marginBottom:
                                                                    '4px',
                                                            }}
                                                            string={
                                                                row.schoolName
                                                            }
                                                        />
                                                    )}
                                                    <Stack
                                                        direction="row"
                                                        spacing={2}
                                                    >
                                                        <ChipSpaced
                                                            data-cy="time"
                                                            icon={
                                                                <AccessTimeIcon />
                                                            }
                                                            label={`${format(
                                                                parseISO(
                                                                    row.startDate
                                                                ),
                                                                'h:mma'
                                                            )} - ${format(
                                                                parseISO(
                                                                    row.endDate
                                                                ),
                                                                'h:mma'
                                                            )}`}
                                                            color="primary"
                                                            variant="outlined"
                                                        />

                                                        <StatusIndicator
                                                            status={row.status}
                                                        />
                                                    </Stack>
                                                </GridItem>
                                            )}
                                        </Hidden>
                                        {/* Show teacher/school name, start time and status and navigation button on xtra small screen */}
                                        <Hidden only={['sm', 'md', 'lg', 'xl']}>
                                            {typeof row.type !== 'undefined' &&
                                            row.type === 'quickBook' ? (
                                                <GridItem
                                                    item
                                                    xs={3}
                                                    padding={2}
                                                >
                                                    <Image
                                                        src={quickBookImage}
                                                    ></Image>
                                                </GridItem>
                                            ) : (
                                                <GridItem item xs={3}>
                                                    {props.type === 'School' ? (
                                                        <Image
                                                            src={
                                                                row.teacherProfile
                                                            }
                                                        />
                                                    ) : (
                                                        <Image
                                                            src={row.schoolLogo}
                                                        />
                                                    )}
                                                </GridItem>
                                            )}

                                            {typeof row.type !== 'undefined' &&
                                            row.type === 'quickBook' ? (
                                                <GridItem
                                                    item
                                                    xs={6}
                                                    sm={3}
                                                    md={2}
                                                    lg={2}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    <QuickBooking
                                                        extraText={true}
                                                    />
                                                </GridItem>
                                            ) : (
                                                <GridItem
                                                    item
                                                    xs={6}
                                                    sm={3}
                                                    md={2}
                                                    lg={2}
                                                    sx={{ textAlign: 'left' }}
                                                >
                                                    {props.type === 'School' ? (
                                                        <TitleCase
                                                            data-cy="teacher-name"
                                                            variant="h6"
                                                            string={
                                                                row.teacherFirstName +
                                                                ' ' +
                                                                row.teacherLastName
                                                            }
                                                        />
                                                    ) : (
                                                        <TitleCase
                                                            variant="h6"
                                                            string={
                                                                row.schoolName
                                                            }
                                                        />
                                                    )}
                                                    <Stack direction="column">
                                                        <Stack direction="row">
                                                            <AccessTimeIcon />
                                                            <Typography variant="body1">
                                                                {format(
                                                                    parseISO(
                                                                        row.startDate
                                                                    ),
                                                                    'h:mma'
                                                                )}
                                                            </Typography>
                                                        </Stack>
                                                        <StatusIndicator
                                                            status={row.status}
                                                            size="small"
                                                        ></StatusIndicator>
                                                    </Stack>
                                                </GridItem>
                                            )}
                                        </Hidden>
                                        {/* Show status on med - xl screens */}
                                        <Hidden only={['xs', 'sm']}>
                                            <GridItem
                                                item
                                                xs={6}
                                                sm={3}
                                                md={2}
                                                lg={2}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    data-cy="status-wide"
                                                >
                                                    {row.status}
                                                </Typography>
                                            </GridItem>
                                        </Hidden>
                                        {/* Grid section containing actions required */}

                                        <GridItem
                                            item
                                            xs={12}
                                            sm={12}
                                            md={3}
                                            lg={3}
                                        >
                                            <AdjustStack
                                                spacing={2}
                                                direction="row"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                            >
                                                {row.status === 'Approved' &&
                                                    typeof row.uploadedDocuments !==
                                                        'undefined' &&
                                                    props.type === 'School' &&
                                                    compareAsc(
                                                        Date.now(),
                                                        parseISO(row.endDate)
                                                    ) === -1 && (
                                                        <Box>
                                                            {!row.uploadedDocuments.some(
                                                                document =>
                                                                    document.thisUser ===
                                                                    true
                                                            ) && (
                                                                <Box>
                                                                    <Typography variant="body1">
                                                                        <Chip
                                                                            label="Class Info Required"
                                                                            variant="outlined"
                                                                            color="primary"
                                                                            sx={{
                                                                                backgroundColor:
                                                                                    '#FFF',
                                                                            }}
                                                                        ></Chip>
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}

                                                {/* Possible duplicate */}

                                                {row.status === 'Approved' &&
                                                    typeof row.uploadedDocuments ===
                                                        'undefined' &&
                                                    props.type === 'School' &&
                                                    compareAsc(
                                                        Date.now(),
                                                        parseISO(row.endDate)
                                                    ) === -1 && (
                                                        <Box>
                                                            {/* Show class info required as a chip on med - xl screens */}

                                                            <Typography variant="body1">
                                                                <Chip
                                                                    label="Class Info Required"
                                                                    data-cy="info-required"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            '#FFF',
                                                                    }}
                                                                ></Chip>
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                {row.status === 'Completed' &&
                                                    props.type === 'School' && (
                                                        <Box
                                                            sx={{
                                                                margin: '4px',
                                                            }}
                                                            data-cy="required"
                                                        >
                                                            {/* Show rating required as a chip on med - xl screens */}

                                                            <Typography variant="body1">
                                                                <Chip
                                                                    label="Rating Required"
                                                                    variant="outlined"
                                                                    color="secondary"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            '#FFF',
                                                                    }}
                                                                ></Chip>
                                                            </Typography>

                                                            {/* Show rating required as text on xs and sm screens */}
                                                        </Box>
                                                    )}

                                                {row.status === 'Approved' &&
                                                    typeof row.teacherFeedback ===
                                                        'undefined' &&
                                                    compareAsc(
                                                        parseISO(row.endDate),
                                                        Date.now()
                                                    ) === -1 &&
                                                    props.type ===
                                                        'Teacher' && (
                                                        <Box
                                                            sx={{
                                                                margin: '4px',
                                                            }}
                                                        >
                                                            {/* Show feedback required as a chip on med - xl screens */}

                                                            <Typography variant="body1">
                                                                <Chip
                                                                    label="Feedback Required"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{
                                                                        backgroundColor:
                                                                            '#FFF',
                                                                    }}
                                                                ></Chip>
                                                            </Typography>

                                                            {/* Show feedback required as text on xs and sm screens */}
                                                        </Box>
                                                    )}

                                                {/* Show approve or reject buttons on all screens */}
                                                {row.status === 'Pending' &&
                                                    compareDesc(
                                                        parseISO(row.startDate),
                                                        Date.now()
                                                    ) === -1 &&
                                                    props.type ===
                                                        'Teacher' && (
                                                        <Stack
                                                            direction={row}
                                                            sx={{
                                                                margin: '4px',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    margin: '4px',
                                                                }}
                                                            >
                                                                <StyledButton
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                    size="medium"
                                                                    sx={{
                                                                        textTransform:
                                                                            'none',
                                                                        backgroundColor:
                                                                            '#FFF',
                                                                    }}
                                                                    startIcon={
                                                                        <CheckCircleOutlineIcon color="sexysuccess" />
                                                                    }
                                                                    onClick={e => {
                                                                        props.confirmBooking(
                                                                            e,
                                                                            true,
                                                                            row
                                                                        );
                                                                    }}
                                                                >
                                                                    Accept
                                                                </StyledButton>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    margin: '4px',
                                                                }}
                                                            >
                                                                <StyledButton
                                                                    color="secondary"
                                                                    variant="outlined"
                                                                    size="medium"
                                                                    sx={{
                                                                        textTransform:
                                                                            'none',
                                                                        backgroundColor:
                                                                            '#FFF',
                                                                    }}
                                                                    startIcon={
                                                                        <RemoveCircleOutlineIcon color="sexyerror" />
                                                                    }
                                                                    onClick={e => {
                                                                        props.confirmBooking(
                                                                            e,
                                                                            false,
                                                                            row
                                                                        );
                                                                    }}
                                                                >
                                                                    Decline
                                                                </StyledButton>
                                                            </Box>
                                                        </Stack>
                                                    )}

                                                {/* Show right navigation button on med - xl screens */}

                                                <Hidden only={['xs', 'sm']}>
                                                    <RightGrowNav />
                                                </Hidden>
                                            </AdjustStack>
                                        </GridItem>
                                    </GridRow>

                                    {row.status === 'Processing' && (
                                        <LinearProgress></LinearProgress>
                                    )}
                                </RootBox>
                            </StyledBox>
                        ))}
                    </BoxShadow>
                </Grid>
            </Grid>
            <SimpleSnackbar
                message={props.snackBarMessage}
                open={props.openSnack}
                severity={props.snackSeverity}
            />
        </Grid>
    );
};

export default HistoryTable;

//2022-10-23T08:00:00
//2022-10-30T08:00:00
